import { useQuery } from 'react-query'

import { useFeatureFlag } from '@webapp/hooks'
import { getExpenseLineItemFormDataUseCase } from '@webapp/modules/expense/business-logic/use-cases/GetExpenseLineItemFormDataUseCase'
import {
  ExpenseFormReimbursementCustomizations,
  type ExpenseFormReimbursementCustomization,
} from '@webapp/modules/expense/entities/enums'
import { type RequestMileageConfiguration } from '@webapp/modules/expense/entities/RequestMileageConfiguration'
import { useGetMileageLockSettingPresenter } from '@webapp/modules/expense/features/get-mileage-lock-setting'
import { expenseReportQueryConfig } from '@webapp/modules/expense/query-configs/expenseReport'
import { type ExpenseMileageConfiguration } from '../../entities'
import { useGetExpenseMileageConfigurationPresenter } from '../../features/get-expense-mileage-configuration'

interface UseEditExpenseLineItemDrawerParams {
  requesterId: number
}

interface UseEditExpenseLineItemDrawerReturn {
  data: {
    reimbursementSetting: ExpenseFormReimbursementCustomization
    mileageLockSetting: RequestMileageConfiguration | null
    mileageConfiguration: Nullable<ExpenseMileageConfiguration>
  }
  states: {
    isFormDataLoading: boolean
  }
}

export const useEditExpenseLineItemDrawer = ({
  requesterId,
}: UseEditExpenseLineItemDrawerParams): UseEditExpenseLineItemDrawerReturn => {
  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )

  const { data, isLoading: isFormDataLoading } = useQuery({
    queryKey: expenseReportQueryConfig.lineItemFormData(requesterId).queryKey,
    queryFn: () =>
      getExpenseLineItemFormDataUseCase.execute({ userId: requesterId }),
  })

  const {
    data: { mileageLockSetting },
    states: { isLoading: isMileageLockSettingLoading },
  } = useGetMileageLockSettingPresenter({
    enabled: !PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS,
  })

  const {
    data: { configuration: mileageConfiguration },
    states: { isLoading: isMileageConfigurationLoading },
  } = useGetExpenseMileageConfigurationPresenter({
    enabled: PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS,
  })

  const reimbursementSetting =
    data?.reimbursementSetting ??
    ExpenseFormReimbursementCustomizations.DEFAULT_YES

  return {
    data: {
      reimbursementSetting,
      mileageLockSetting,
      mileageConfiguration,
    },
    states: {
      isFormDataLoading:
        isFormDataLoading ||
        isMileageLockSettingLoading ||
        isMileageConfigurationLoading,
    },
  }
}
